import i18n from '../../i18n';
import type { DatePrecision, Infestation } from './api';

const number = (digits: number) =>
  new Intl.NumberFormat('de-DE', { maximumFractionDigits: digits });

/** e.g. "2,5". */
export function formatAmount(value: number, digits = 2): string {
  return number(digits).format(value);
}

/** Age of a colony (VOL-003): "2 Jahre 3 Monate" or "Jahrgang 2024". */
export function formatAge(
  originDate: string,
  precision: DatePrecision,
  today = new Date(),
): string {
  const origin = new Date(`${originDate}T00:00:00`);
  if (precision === 'year') {
    return i18n.t('apiary.age.vintage', { year: origin.getFullYear() });
  }
  let months =
    (today.getFullYear() - origin.getFullYear()) * 12 + today.getMonth() - origin.getMonth();
  if (precision === 'day' && today.getDate() < origin.getDate()) months -= 1;
  months = Math.max(0, months);
  const years = Math.floor(months / 12);
  const rest = months % 12;
  if (years === 0) return i18n.t('apiary.age.months', { count: rest });
  if (rest === 0) return i18n.t('apiary.age.years', { count: years });
  return i18n.t('apiary.age.yearsMonths', {
    years: i18n.t('apiary.age.years', { count: years }),
    months: i18n.t('apiary.age.months', { count: rest }),
  });
}

/** The origin date as precise as it is known. */
export function formatOriginDate(date: string, precision: DatePrecision): string {
  const d = new Date(`${date}T00:00:00`);
  if (precision === 'year') return String(d.getFullYear());
  if (precision === 'month') {
    return new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(d);
  }
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }).format(d);
}

/** "8,3 Milben/Tag" or "3 % Befall". */
export function formatInfestation(i: Infestation): string {
  return i.unit === 'percent'
    ? i18n.t('apiary.miteCounts.percent', { value: formatAmount(i.value, 1) })
    : i18n.t('apiary.miteCounts.perDay', { value: formatAmount(i.value, 1) });
}

/** Days since an ISO date (for running treatments). */
export function daysSince(date: string, today = new Date()): number {
  const start = new Date(`${date}T00:00:00`);
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.max(0, Math.round((t.getTime() - start.getTime()) / 86_400_000));
}

/** "Juli 2026" – timeline groups. */
export function monthLabel(date: string): string {
  return new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(
    new Date(`${date}T00:00:00`),
  );
}
